import DevAppAPI from "../api/DevApp";
import { API } from "../api/api";
import { DEV_CONSOLE_URL, APP_MANAGEMENT_URL } from "../config";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { hideLoader, showLoader } from "../util/loader/CustomLoader";
import { CustomToast } from "../util/custom-toast/CustomToast";
import { useHistory } from "react-router";
import { appStatus } from "../util/helpers/statusHelper";
import { getAppStoreData, getMsg } from "../util/helpers/funcHelper";
import ConsoleLocalStorage from "../storage/storage";

//convert date object to string
const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
function formatDate(d) {
  let date = new Date(d);
  var dd = date.getDate();
  var mm = monthNames[date.getMonth()];
  var yyyy = date.getFullYear();
  if (dd < 10) {
    dd = "0" + dd;
  }
  if (mm < 10) {
    mm = "0" + mm;
  }
  return (d = dd + " " + mm + " " + yyyy);
}

/**
 * @desc creates a new app
 */
export const useCreateApp = (projectid, setShowNewAppModal) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async (values) => {
      const api = new DevAppAPI();
      return await api.amCreateApp(projectid, values);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: (data) => {
        hideLoader();
        setShowNewAppModal(false);
        CustomToast("App creation has been initiated");
        if (data.data.data) {
          const { id, projectId } = data.data.data;
          history.push(`/console/${projectId}/${id}/store-listing`);
        }
        queryClient.invalidateQueries("allapps");
      },
      onError: (error) => {
        hideLoader();
        if (error.response.data.error_code === 500) {
          CustomToast("Please select app Category");
        } else if (error.response.data.error_code === "E05025") {
          CustomToast(error.response.data?.message);
        } else {
          CustomToast(getMsg(error.response.data.error_code));
        }
      },
    }
  );
  return { mutate, isSuccess, isError, isLoading, error };
};

/**
 * @returns one app details
 */
export const useOneApp = (appid) => {
  const history = useHistory();
  const { data, isLoading, isError, refetch } = useQuery(
    ["oneapp", appid],
    async () => {
      let api = new DevAppAPI();
      const res = await api.getOneApp(appid);
      const app = {
        ...res.data.data,
        created_at: formatDate(res.data.data.created_at),
      };
      // console.log(app);
      return app;
    },
    {
      onError: (error) => {
        history.replace(history.location.pathname, {
          errorStatusCode: error.response ? error.response.status : 500,
        });
      },
    }
  );
  return { data, isLoading, isError, refetch };
};

/**
 * @returns an array of all apps in a project
 */
export const useAllApps = (projectid, platformType, projectStatus, appOs) => {
  const history = useHistory();
  const { mutate, data, isLoading, isError } = useMutation(
    ["allapps", projectid, platformType, projectStatus],
    async () => {
      let api = new DevAppAPI();
      const res = await api.getAllApps(
        projectid,
        platformType,
        projectStatus,
        appOs
      );
      const apps = res?.data?.data?.map((app) => ({
        ...app,
        created_at: formatDate(app.created_at),
      }));
      return apps;
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
      },
      onError: (error) => {
        history.replace(history.location.pathname, {
          errorStatusCode: error.response ? error.response.status : 500,
        });
      },
    }
  );
  return { mutate, data, isLoading, isError };
};

/**
 * @returns  all the apps that are present in an organisation
 * Not used anywhere
 */
export const useAllOrgApps = () => {
  const history = useHistory();
  const { data, isLoading, isError } = useQuery(
    "allorgapps",
    async () => {
      let api = new DevAppAPI();
      const res = await api.getAllOrgApps();
      const apps = res.data.data.map((app) => ({
        ...app,
        created_at: formatDate(app.created_at),
      }));
      return apps;
    },
    {
      onError: (error) => {
        history.replace(history.location.pathname, {
          errorStatusCode: error.response ? error.response.status : 500,
        });
      },
    }
  );
  return { data, isLoading, isError };
};

/**
 * @desc updates store listing of a particular app
 * Language conversion to array only works if langs are separated by commas
 */
export const useUpdateAppStoreListing = (appid) => {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async (values) => {
      const data = {
        title: values.title,
        category: values.category,
        shortDescription: values.shortDescription,
        description: values.description,
        ageRating: values.ageRating,
        languages: values.languages.split(","),
        privacyPolicy: values.privacyPolicy,
        controllerRequired: values.controllerRequired,
        interactionType: values.interactionType,
        platform: values.platform,
        ...(values.appSign && { appSign: values.appSign }),
      };
      const api = new DevAppAPI();

      await api.updateApp(appid, data);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Store listing details updated");
        queryClient.invalidateQueries("oneapp");
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isSuccess, isError, isLoading, error };
};

/**
 * @desc add/update icon, banner, vids and and other graphics for an app
 */
export const useUploadGraphics = (appid) => {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    ["uploadGraphic", appid],
    async (values) => {
      // const data = {
      //   ...values.graphics,
      //   // video: values.graphics.video.filter((bn) => bn.url !== "" && bn.url !== undefined),
      //   screenshots: values.graphics.screenshots.filter(
      //     (ss) => ss.url !== "" && ss.url !== undefined
      //   ),
      // };
      const api = new DevAppAPI();
      await api.uploadGraphics(appid, values);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Graphics uploaded");
        queryClient.invalidateQueries("oneapp");
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isSuccess, isError, isLoading, error };
};

/**
 * @desc appstore graphics update add/update icon, banner, vids and and other graphics for an app
 */
export const useUpdateAppstoreGraphics = () => {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async (values) => {
      const api = new DevAppAPI();
      await api.updateAppstoreGraphics(values);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Graphics uploaded in JioImmerse Appstore");
        queryClient.invalidateQueries("oneapp", { exact: true });
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isSuccess, isError, isLoading, error };
};
/**
 * @desc create an apk release for a particular app
 */
export const useReleaseApk = (appid) => {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async (values) => {
      // const data = {
      //   url: values.createRelease.url,
      //   version: values.createRelease.version,
      //   releaseNote: values.createRelease.releaseNote,
      //   size: Math.floor(values.createRelease.size / 1024),
      // };
      const api = new DevAppAPI();
      await api.releaseApp(appid, { ...values, size: Math.floor(values.size / 1024) });
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Build released");
        queryClient.invalidateQueries("buildstatus", { exact: true });
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );

  return { mutate, isSuccess, isError, isLoading, error };
};

/**
 * @returns an array of all the builds of the application created since the creation of app
 */
export const useBuildStatus = (appid) => {
  const history = useHistory();
  const { data, isLoading, isError } = useQuery(
    "buildstatus",
    async () => {
      let api = new DevAppAPI();
      const res = await api.getBuildStatus(appid);
      let buildData = res.data.data;
      let indexOfActive =
        buildData &&
        buildData
          .map((object) => object.status)
          .indexOf(appStatus.release.activated.status);
      return res.data.data.map((build, index) => ({
        ...build,
        created_at: formatDate(build.created_at),
        updated_at: formatDate(build.updated_at),
        isRollback: index > indexOfActive ? false : true,
      }));
    },
    {
      onError: (error) => {
        history.replace(history.location.pathname, {
          errorStatusCode: error.response ? error.response.status : 500,
        });
      },
    }
  );
  return { data, isLoading, isError };
};

/**
 * @desc delete a particular build
 */
export const useDeleteBuild = (releaseid) => {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async () => {
      const api = new DevAppAPI();
      await api.deleteBuild(releaseid);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Build deleted");
        queryClient.invalidateQueries("buildstatus", { exact: true });
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isError, isLoading, isSuccess, error };
};

/**
 *
 * @desc publish a particular build that has been created by release apk api call
 */
export const usePublishBuild = () => {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async (releaseid) => {
      const api = new DevAppAPI();
      await api.publishBuild(releaseid);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Build submitted for review");
        queryClient.invalidateQueries("buildstatus", { exact: true });
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isError, isLoading, isSuccess, error };
};

export const useRepublishBuild = () => {
  const queryClient = useQueryClient();
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async (payload) => {
      const { releaseid, publishType } = payload;
      const api = new DevAppAPI();
      await api.republish(releaseid, publishType);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Build submitted");
        queryClient.invalidateQueries("buildstatus", { exact: true });
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isError, isLoading, isSuccess, error };
};

// get Project and App Category
export const useGetCategoryList = (
  categoryType,
  platformType,
  appOS = "android"
) => {
  const { data, isLoading, isError } = useQuery(
    ["getCategory", categoryType],
    async () => {
      // let api = new DevAppAPI();
      let res;
      if (categoryType === "project") {
        res = await API(`${APP_MANAGEMENT_URL}project-category`, "get");
      } else if (categoryType === "app") {
        let url = `${APP_MANAGEMENT_URL}category?appOS=${appOS}&tags=${ConsoleLocalStorage.getOrganization() &&
          ConsoleLocalStorage.getOrganization().tags
          ? ConsoleLocalStorage.getOrganization().tags
          : "consumer"
          }`;
        res = await API(url, "get");
      }
      return res.data.data;
      // const builds = res.data.data.map((build) => ({
      //   ...build,
      //   created_at: formatDate(build.created_at),
      //   updated_at: formatDate(build.updated_at),
      // }));
    }
  );
  return { data, isLoading, isError };
};

export const useGetDebugProdKey = () => {
  const { mutate, isSuccess, isError, isLoading, error, data } = useMutation(
    async (values) => {
      const api = new DevAppAPI();
      return await api.getKeys(values);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        CustomToast("Key generated");
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isError, isLoading, isSuccess, error, data };
};

export const useUpdateAppDetails = (appid) => {
  const { mutate, isSuccess, isError, isLoading, error } = useMutation(
    async (values) => {
      const api = new DevAppAPI();
      const data = {
        appSign: values.appSign,
        // appOS:values.appOS,
        storeData: getAppStoreData(values),
      };
      await api.updateApp(appid, data);
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
        // useReleaseApk(appid)
        CustomToast("App Details Updated");
      },
      onError: (error) => {
        hideLoader();
        CustomToast(error.response.data.message);
      },
    }
  );
  return { mutate, isError, isLoading, isSuccess, error };
};

export const useGetAppKeys = (appid, keyType) => {
  const { mutate, isSuccess, isError, isLoading, error, data } = useMutation(
    async () => {
      const api = new DevAppAPI();
      return await api.getAppKeys(appid, keyType.toUpperCase());
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: (data) => {
        hideLoader();
        // CustomToast("App keys retrieved!");
      },
      onError: (error) => {
        hideLoader();
        // CustomToast(getMsg(error.response.data.error_code));
      },
    }
  );
  return { mutate, isError, isLoading, isSuccess, error, data };
};

export const useAppConfig = (currentOs) => {
  const { mutate, isSuccess, isError, isLoading, error, data } = useQuery(
    "appConfig",
    async () => {
      const api = new DevAppAPI();
      const res = await api.getAppCreationConfig(currentOs);
      return res.data.data;
    },
    {
      onMutate: () => {
        showLoader();
      },
      onSuccess: () => {
        hideLoader();
      },
      onError: () => {
        hideLoader();
      },
    }
  );
  return { mutate, isSuccess, isError, isLoading, error, data };
};
